import { useCallback, useMemo, type ComponentProps } from "react";
import { generatePath, Link } from "react-router";

import { PATHS } from "@/routes/paths";

import type {
	SelectableSnackItemType,
	SnackBlocksFormModelContentsMethodsType,
} from "@/components/features/snack/snack/settings/model/usecase/use-create-snack-layout-settings-form-model/methods/contents";

import { Dialog, DialogClose, DialogContent, DialogTitle } from "@/components/ui/dialog/dialog";
import { Image } from "@/components/ui/image/image";

type Props = {
	hotelID: string;
	open: ComponentProps<typeof Dialog>["open"];
	handleOpenChange: ComponentProps<typeof Dialog>["onOpenChange"];

	handleClickCloseButton: ComponentProps<typeof DialogClose>["onClick"];

	selectableSnacks: SnackBlocksFormModelContentsMethodsType["selectableSnacks"];
	deactivateSelectSnackModal: () => void;

	onSelectSnack: ({ selectedSnack }: { selectedSnack: SelectableSnackItemType }) => void;
};

export const DialogSelectSnack = ({
	hotelID,
	open,
	handleOpenChange,
	handleClickCloseButton,
	selectableSnacks,
	onSelectSnack,
	deactivateSelectSnackModal,
}: Props) => {
	const handleSelectSnack = useCallback(
		(snack: SelectableSnackItemType) => (_: React.MouseEvent) => {
			onSelectSnack({ selectedSnack: snack });
			deactivateSelectSnackModal();
		},
		[deactivateSelectSnackModal, onSelectSnack],
	);

	const snackExists = useMemo(() => {
		return selectableSnacks.length > 0;
	}, [selectableSnacks]);

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogClose onClick={handleClickCloseButton} />

			<DialogContent className="max-w-[840px] overflow-y-auto">
				<div className="space-y-12">
					<DialogTitle>スナック一覧</DialogTitle>

					{snackExists ? (
						<div className="grid grid-cols-4 gap-4">
							{selectableSnacks.map((snack) => {
								const { title, thumbnail, uuid } = snack;

								return (
									<div
										role="button"
										key={uuid}
										className="group col-span-1 h-fit rounded border border-zinc-100"
										onClick={handleSelectSnack(snack)}
									>
										<Image
											src={thumbnail}
											className="h-24 w-full rounded-t object-cover group-hover:opacity-80"
										/>
										<p className="line-clamp-3 break-words px-1 pb-3 pt-2 text-sm group-hover:bg-zinc-100">
											{title}
										</p>
									</div>
								);
							})}
						</div>
					) : (
						<div className="space-y-2">
							<p>スナックがまだ作成されていません</p>

							<div className="flex items-center gap-x-1">
								<p>スナックを作成する場合は</p>
								<Link
									to={generatePath(PATHS.HOTEL.RESERVATION_PAGE.SNACK.LIST, { hotelID })}
									target="_blank"
									className="text-link-400 underline-offset-4 hover:underline"
								>
									こちら
								</Link>
								<p>から</p>
							</div>
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};

DialogSelectSnack.displayName = "DialogSelectSnack";
